import { useState } from 'react';
import jsPDF from 'jspdf';
import { STYLES, STYLE_ORDER, StyleKey } from '../../data/styles';
import { GetResultResponse } from '../../types';

interface DownloadPdfButtonProps {
  result: GetResultResponse;
}

export function DownloadPdfButton({ result }: DownloadPdfButtonProps) {
  const [generating, setGenerating] = useState(false);

  function generatePdf() {
    setGenerating(true);

    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const dominant = STYLES[result.dominantStyle];
      const backup = STYLES[result.backupStyle];
      const total = Object.values(result.scores).reduce((a, b) => a + b, 0);
      const sorted = [...STYLE_ORDER].sort(
        (a, b) => result.scores[b] - result.scores[a]
      );

      // --- Header ---
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(27, 40, 69);
      doc.text('Social Style Profile', margin, y);
      y += 10;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(115, 123, 129);
      doc.text(result.name, margin, y);
      y += 6;

      doc.setFontSize(9);
      doc.setTextColor(140, 140, 140);
      const dateStr = new Date(result.submittedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      doc.text(`Completed on ${dateStr}`, margin, y);
      y += 12;

      // --- Divider ---
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;

      // --- Score Breakdown ---
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(27, 40, 69);
      doc.text('Score Breakdown', margin, y);
      y += 8;

      for (const key of sorted) {
        const style = STYLES[key];
        const pct = total > 0 ? Math.round((result.scores[key] / total) * 100) : 0;

        // Check if we need a new page
        if (y > 270) {
          doc.addPage();
          y = margin;
        }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(style.name, margin, y);
        doc.text(`${result.scores[key]} / ${total}  (${pct}%)`, pageWidth - margin, y, { align: 'right' });
        y += 4;

        // Score bar background
        const barHeight = 4;
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(margin, y, contentWidth, barHeight, 2, 2, 'F');

        // Score bar fill
        const rgb = hexToRgb(style.color);
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        const barWidth = Math.max(contentWidth * (pct / 100), 2);
        doc.roundedRect(margin, y, barWidth, barHeight, 2, 2, 'F');
        y += 10;
      }

      y += 6;

      // --- Dominant Style ---
      y = renderStyleSection(doc, dominant, result.dominantStyle, 'Dominant Style', result.scores, total, margin, contentWidth, y, true);

      // --- Backup Style ---
      y = renderStyleSection(doc, backup, result.backupStyle, 'Backup Style', result.scores, total, margin, contentWidth, y, false);

      // --- Footer ---
      y += 5;
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text('Social Style Assessment by LINX Consulting', margin, y);

      doc.save(`${result.name.replace(/\s+/g, '_')}_Social_Style_Profile.pdf`);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <button
      onClick={generatePdf}
      disabled={generating}
      className="
        inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-brand/20
        text-brand font-medium text-sm
        hover:border-brand/40 hover:bg-porcelain
        focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:ring-offset-2
        transition-all duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
      "
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
        <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
      </svg>
      {generating ? 'Generating...' : 'Download PDF'}
    </button>
  );
}

function renderStyleSection(
  doc: jsPDF,
  style: (typeof STYLES)[StyleKey],
  styleKey: StyleKey,
  label: string,
  scores: GetResultResponse['scores'],
  total: number,
  margin: number,
  contentWidth: number,
  y: number,
  showDetails: boolean
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const rgb = hexToRgb(style.color);

  // Section label
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(rgb.r, rgb.g, rgb.b);
  doc.text(label.toUpperCase(), margin, y);
  y += 6;

  // Style name
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(27, 40, 69);
  doc.text(`${style.name} — "${style.nickname}"`, margin, y);

  // Score
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(140, 140, 140);
  doc.text(`${scores[styleKey]} / ${total}`, pageWidth - margin, y, { align: 'right' });
  y += 8;

  // Summary
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  const summaryLines = doc.splitTextToSize(style.summary, contentWidth);
  doc.text(summaryLines, margin, y);
  y += summaryLines.length * 5 + 4;

  if (showDetails) {
    // Traits
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 140, 140);
    doc.text('KEY TRAITS', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(style.traits.join('  ·  '), margin, y);
    y += 7;

    // Positive perceptions
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 140, 140);
    doc.text('SEEN POSITIVELY AS', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(style.positivePerceptions.join('  ·  '), margin, y);
    y += 7;

    // Stress perceptions
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 140, 140);
    doc.text('UNDER STRESS SEEN AS', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(style.stressPerceptions.join('  ·  '), margin, y);
    y += 7;

    // High wants
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 140, 140);
    doc.text('MOTIVATED BY', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(style.highWants.join('  ·  '), margin, y);
    y += 7;
  }

  y += 6;
  return y;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}
